import { Collection } from '@msw/data';
import z from 'zod';

import {
  CAMPAIGN_POST_SORT,
  CampaignStatus,
  CampaignTrackingItemType,
  getCampaignOperationResponseSchema,
  isCampaignSupportedPlatform,
  listCampaignCreatorsOperationResponseSchema,
  listCampaignPostsOperationResponseSchema,
} from '@/entities/campaign';
import { CAMPAIGN_LABEL_TYPE } from '@/entities/campaign/constant';
import { SOURCE_PLATFORM_TYPE } from '@/entities/public-filter/public-filter-constants';

import { campaignCollection } from './campaign';

const CampaignCreatorSchema = z.object({
  id: z.string(),
  campaign_id: z.string(),
  name: z.string(),
  handle: z.string(),
  avatar_url: z.string().optional(),
  email: z.string().nullable().optional(),
  label: z.string().nullable().optional(),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  profile_url: z.string(),
});

const CampaignPostSchema = z.object({
  id: z.string(),
  campaign_id: z.string(),
  creator_id: z.string(),
  creator_name: z.string(),
  creator_label: z.string().nullable().optional(),
  platform: z.enum(SOURCE_PLATFORM_TYPE),
  caption: z.string(),
  post_url: z.string(),
  posted_at: z.string(),
  metrics_updated_at: z.string(),
  is_manual: z.boolean(),
  is_excluded: z.boolean(),
  metrics: z.object({
    views: z.number().int().nonnegative(),
    likes: z.number().int().nonnegative(),
    comments: z.number().int().nonnegative(),
    shares: z.number().int().nonnegative(),
    engagements: z.number().int().nonnegative(),
    er: z.number().nonnegative(),
  }),
});

export const campaignCreatorCollection = new Collection({
  schema: CampaignCreatorSchema,
});

export const campaignPostCollection = new Collection({
  schema: CampaignPostSchema,
});

type RecentWindowCampaign = {
  last_collected_at?: string | null;
  status: CampaignStatus | string;
};

type RecentWindowPost = {
  is_excluded: boolean;
  posted_at: string;
};

export function getRecentPostsWindow(campaign: RecentWindowCampaign, now = new Date()) {
  if (campaign.status === CampaignStatus.SCHEDULED && !campaign.last_collected_at) return null;

  const fallbackEnd = now;
  const parsedEnd = campaign.last_collected_at ? new Date(campaign.last_collected_at) : fallbackEnd;
  const end = Number.isNaN(parsedEnd.getTime()) ? fallbackEnd : parsedEnd;
  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 1);

  return { end, start };
}

export function isPostInRecentWindow(
  post: RecentWindowPost,
  campaign: RecentWindowCampaign,
  now = new Date(),
) {
  if (post.is_excluded) return false;

  const window = getRecentPostsWindow(campaign, now);
  if (!window) return false;

  const postedAt = new Date(post.posted_at);
  if (Number.isNaN(postedAt.getTime())) return false;

  return postedAt >= window.start && postedAt <= window.end;
}

const creatorNamePool = [
  'Mia Johnson',
  'Ethan Park',
  'Sophia Rivera',
  'Noah Kim',
  'Ava Collins',
  'Liam Carter',
  'Olivia Davis',
  'Lucas Walker',
  'Emma Brooks',
  'James Reed',
];

const captionPool = [
  'Daily routine feature with direct product mention and creator POV.',
  'Quick how-to clip with strong hook in the first three seconds.',
  'Unboxing post that highlights packaging and creator reaction.',
  'Before and after story with detailed caption and CTA.',
  'Lifestyle roundup post mixing campaign keyword and creator voice.',
  'Event recap that tags the brand and campaign hashtag clearly.',
];

export async function initMockCampaignDetailDB() {
  campaignCreatorCollection.clear();
  campaignPostCollection.clear();

  for (const campaign of campaignCollection.findMany()) {
    await seedCampaignDetail(campaign);
  }
}

export async function seedCampaignDetail(
  campaign: ReturnType<typeof campaignCollection.findMany>[number],
) {
  const platforms =
    campaign.platforms.length > 0
      ? campaign.platforms
      : [SOURCE_PLATFORM_TYPE.TIKTOK, SOURCE_PLATFORM_TYPE.INSTAGRAM, SOURCE_PLATFORM_TYPE.YOUTUBE];

  const creators = Array.from({ length: Math.max(campaign.creator_count, 0) }).map((_, index) => {
    const baseName = creatorNamePool[index % creatorNamePool.length] ?? `Creator ${index + 1}`;
    const handle = normalizeHandle(`@${baseName.toLowerCase().replace(/\s+/g, '')}${index + 1}`);
    const platform = platforms[index % platforms.length] ?? SOURCE_PLATFORM_TYPE.TIKTOK;

    return {
      id: `creator-seed-${campaign.id}-${index + 1}`,
      campaign_id: campaign.id,
      name: baseName,
      handle,
      avatar_url: '',
      email: index % 3 === 2 ? null : `${handle}@brandmail.com`,
      label:
        [CAMPAIGN_LABEL_TYPE.AFFILIATE, CAMPAIGN_LABEL_TYPE.GIFTING, CAMPAIGN_LABEL_TYPE.CASH_PAID][
          index % 3
        ] ?? '',
      platform,
      profile_url: buildProfileUrl(platform, handle),
    };
  });

  for (const creator of creators) {
    await campaignCreatorCollection.create(creator);
  }

  const creatorSource = creators.length > 2 ? creators.slice(0, creators.length - 2) : creators;
  const posts = Array.from({ length: Math.max(campaign.post_count, 0) }).map((_, index) => {
    const creator = creatorSource[index % Math.max(creatorSource.length, 1)] ?? creators[0];
    const platform = creator?.platform ?? platforms[0] ?? SOURCE_PLATFORM_TYPE.TIKTOK;
    const metrics = buildPostMetrics(index + 1, platform);
    const postedAt = new Date(Date.now() - index * 1000 * 60 * 60 * 18).toISOString();

    return {
      id: `post-seed-${campaign.id}-${index + 1}`,
      campaign_id: campaign.id,
      creator_id: creator?.id ?? `creator-fallback-${campaign.id}`,
      creator_name: creator?.name ?? 'Creator',
      creator_label: creator?.label ?? '',
      platform,
      caption: captionPool[index % captionPool.length] ?? 'Campaign post',
      post_url: buildPostUrl(platform, creator?.handle ?? 'creator', index + 1),
      posted_at: postedAt,
      metrics_updated_at: new Date(Date.now() - index * 1000 * 60 * 60 * 6).toISOString(),
      is_manual: index % 4 === 0,
      is_excluded: false,
      metrics,
    };
  });

  for (const post of posts) {
    await campaignPostCollection.create(post);
  }

  syncCampaignSummary(campaign.id);
}

function buildCampaignDetailSnapshot(campaignId: string) {
  const campaign = campaignCollection.findFirst((query) => query.where({ id: campaignId }));
  if (!campaign) return null;

  const creators = campaignCreatorCollection
    .findMany()
    .filter((creator) => creator.campaign_id === campaignId)
    .sort((left, right) => left.name.localeCompare(right.name));

  const posts = campaignPostCollection
    .findMany()
    .filter((post) => post.campaign_id === campaignId)
    .sort((left, right) => right.posted_at.localeCompare(left.posted_at));

  return {
    campaign: mapCampaignToDTO(campaign),
    creators: creators.map((creator) => ({
      id: creator.id,
      name: creator.name,
      handle: creator.handle,
      avatar_url: creator.avatar_url,
      email: creator.email ?? null,
      label: creator.label ?? '',
      platform: creator.platform,
      profile_url: creator.profile_url,
      post_count: posts.filter((post) => !post.is_excluded && post.creator_id === creator.id)
        .length,
    })),
    posts: posts.map((post) => ({
      id: post.id,
      creator_id: post.creator_id,
      creator_name: post.creator_name,
      creator_label: post.creator_label ?? '',
      creator: {
        id: post.creator_id,
        creator_name: post.creator_name,
        handle:
          creators.find((creator) => creator.id === post.creator_id)?.handle ??
          post.creator_name.toLowerCase().replace(/\s+/g, ''),
        avatar_url: creators.find((creator) => creator.id === post.creator_id)?.avatar_url ?? '',
        profile_url:
          creators.find((creator) => creator.id === post.creator_id)?.profile_url ?? post.post_url,
      },
      platform: post.platform,
      caption: post.caption,
      post_url: post.post_url,
      posted_at: post.posted_at,
      metrics_updated_at: post.metrics_updated_at,
      is_manual: post.is_manual,
      is_excluded: post.is_excluded,
      metrics: post.metrics,
    })),
  };
}

export function buildCampaignDetailResponse(campaignId: string) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  return getCampaignOperationResponseSchema.parse({
    id: detail.campaign.id,
    name: detail.campaign.name,
    start_date: new Date(detail.campaign.start_date).toISOString(),
    end_date: detail.campaign.end_date
      ? new Date(detail.campaign.end_date).toISOString()
      : detail.campaign.end_date,
    keywords: detail.campaign.keywords,
    hashtags: detail.campaign.hashtags,
    mentions: detail.campaign.mentions,
    platforms: detail.campaign.platforms,
    status: detail.campaign.status,
    created_at: detail.campaign.created_at,
    updated_at: detail.campaign.updated_at ?? detail.campaign.created_at,
    items: [
      ...detail.creators
        .filter((creator) => isCampaignSupportedPlatform(creator.platform))
        .map((creator) => ({
          id: creator.id,
          type: CampaignTrackingItemType.HANDLE,
          handle: creator.handle,
          platform: creator.platform,
          label: creator.label?.trim() ? creator.label : undefined,
          name: creator.name,
          email: creator.email ?? undefined,
        })),
      ...detail.posts.map((post) => ({
        id: post.id,
        type: CampaignTrackingItemType.POST,
        post_url: post.post_url,
      })),
    ],
  });
}

export function buildCampaignOverviewResponse(campaignId: string) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  const activePosts = detail.posts.filter((post) => !post.is_excluded);
  const postedAccountCount = new Set(activePosts.map((post) => post.creator_id)).size;
  const recentPosts = detail.posts.filter((post) => isPostInRecentWindow(post, detail.campaign));

  return {
    campaign_creators_count: detail.creators.length,
    campaign_creators_with_post_count: postedAccountCount,
    tracked_posts_count: activePosts.length,
    recent_posts_count: recentPosts.length,
  };
}

export function buildCampaignMetricResponse(campaignId: string) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  const activePosts = detail.posts.filter((post) => !post.is_excluded);

  return {
    average_er:
      activePosts.length === 0
        ? 0
        : activePosts.reduce((acc, post) => acc + post.metrics.er, 0) / activePosts.length,
    total_comments: activePosts.reduce((acc, post) => acc + post.metrics.comments, 0),
    total_creators_count: detail.creators.length,
    total_engagements: activePosts.reduce((acc, post) => acc + post.metrics.engagements, 0),
    total_likes: activePosts.reduce((acc, post) => acc + post.metrics.likes, 0),
    total_shares: activePosts.reduce((acc, post) => acc + post.metrics.shares, 0),
    total_views: activePosts.reduce((acc, post) => acc + post.metrics.views, 0),
  };
}

export function buildCampaignCreatorsResponse(
  campaignId: string,
  request?: {
    platform?: string;
    label?: string;
    name?: string;
    pending?: boolean;
    page?: number;
    limit?: number;
  },
) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  const activePosts = detail.posts.filter((post) => !post.is_excluded);
  const normalizedName = request?.name?.trim().toLowerCase();
  const creatorsWithPostCount = detail.creators.map((creator) => ({
    ...creator,
    post_count: activePosts.filter((post) => post.creator_id === creator.id).length,
  }));
  const filteredCreators = creatorsWithPostCount.filter((creator) => {
    if (request?.platform && creator.platform !== request.platform) return false;
    if (request?.label !== undefined && creator.label !== request.label) return false;

    if (normalizedName) {
      const matchesSearch = [creator.name, creator.handle, creator.email ?? ''].some((value) =>
        value.toLowerCase().includes(normalizedName),
      );
      if (!matchesSearch) return false;
    }

    if (request?.pending && creator.post_count > 0) return false;

    return true;
  });

  const page = request?.page ?? 1;
  const limit = request?.limit ?? Math.max(filteredCreators.length, 1);
  const startIndex = (page - 1) * limit;

  return listCampaignCreatorsOperationResponseSchema.parse({
    items: filteredCreators.slice(startIndex, startIndex + limit).map((creator) => ({
      id: creator.id,
      platform: creator.platform,
      username: creator.handle.replace(/^@+/, ''),
      nickname: creator.name,
      profile_image_url: creator.avatar_url ?? null,
      email: creator.email,
      label: resolveCampaignLabel(creator.label) ?? null,
      post_count: creator.post_count,
      created_at: detail.campaign.created_at,
    })),
    pagination: {
      page,
      limit,
      total: filteredCreators.length,
    },
  });
}

export function buildCampaignCreatorCountsResponse(
  campaignId: string,
  request?: {
    platform?: string;
    label?: string;
    name?: string;
    pending?: boolean;
  },
) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  const activePosts = detail.posts.filter((post) => !post.is_excluded);
  const normalizedName = request?.name?.trim().toLowerCase();
  const filteredCreators = detail.creators.filter((creator) => {
    if (request?.platform && creator.platform !== request.platform) return false;
    if (request?.label !== undefined && creator.label !== request.label) return false;

    if (normalizedName) {
      const matchesSearch = [creator.name, creator.handle, creator.email ?? ''].some((value) =>
        value.toLowerCase().includes(normalizedName),
      );
      if (!matchesSearch) return false;
    }

    if (request?.pending && activePosts.some((post) => post.creator_id === creator.id)) {
      return false;
    }

    return true;
  });

  return {
    count: filteredCreators.length,
  };
}

export function buildCampaignPostsResponse(campaignId: string) {
  return buildCampaignPostsListResponse(campaignId);
}

export function buildCampaignPostsListResponse(
  campaignId: string,
  request?: {
    recent?: boolean;
    sort?: CAMPAIGN_POST_SORT;
    page?: number;
    limit?: number;
  },
) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  const filteredPosts = detail.posts
    .filter((post) => !post.is_excluded)
    .filter((post) => (request?.recent ? isPostInRecentWindow(post, detail.campaign) : true));
  const sortedPosts = [...filteredPosts].sort((left, right) => {
    const sortBy = request?.sort ?? CAMPAIGN_POST_SORT.POSTED_AT_DESC;

    if (sortBy === CAMPAIGN_POST_SORT.VIEW_DESC) return right.metrics.views - left.metrics.views;
    if (sortBy === CAMPAIGN_POST_SORT.COMMENT_DESC)
      return right.metrics.comments - left.metrics.comments;
    if (sortBy === CAMPAIGN_POST_SORT.LIKE_DESC) return right.metrics.likes - left.metrics.likes;
    if (sortBy === CAMPAIGN_POST_SORT.SHARED_DESC)
      return right.metrics.shares - left.metrics.shares;
    if (sortBy === CAMPAIGN_POST_SORT.RATE_DESC) return right.metrics.er - left.metrics.er;
    return right.posted_at.localeCompare(left.posted_at);
  });

  const page = request?.page ?? 1;
  const limit = request?.limit ?? Math.max(sortedPosts.length, 1);
  const startIndex = (page - 1) * limit;

  return listCampaignPostsOperationResponseSchema.parse({
    items: sortedPosts.slice(startIndex, startIndex + limit).map((post) => ({
      campaign_post_id: post.id,
      label: resolveCampaignLabel(post.creator_label),
      post: buildCampaignPostListItem(post),
      is_new: isPostInRecentWindow(post, detail.campaign),
      source: post.is_manual ? 'manual' : 'auto',
    })),
    pagination: {
      page,
      limit,
      total: sortedPosts.length,
    },
  });
}

export function buildCampaignPostCountsResponse(campaignId: string, recent = false) {
  const detail = buildCampaignDetailSnapshot(campaignId);
  if (!detail) return null;

  const activePosts = detail.posts.filter((post) => !post.is_excluded);
  const filteredPosts = recent
    ? activePosts.filter((post) => isPostInRecentWindow(post, detail.campaign))
    : activePosts;

  return {
    count: filteredPosts.length,
  };
}

export function syncCampaignSummary(campaignId: string) {
  const campaign = campaignCollection.findFirst((query) => query.where({ id: campaignId }));
  if (!campaign) return null;

  const creators = campaignCreatorCollection
    .findMany()
    .filter((creator) => creator.campaign_id === campaignId);
  const posts = campaignPostCollection.findMany().filter((post) => post.campaign_id === campaignId);

  const platforms = Array.from(
    new Set([
      ...creators.map((creator) => creator.platform),
      ...posts.map((post) => post.platform),
    ]),
  );
  const nextTrackedItemCount = creators.length + posts.length;
  const previewCreators = creators.slice(0, 3).map((creator) => ({
    name: creator.name,
    profile_image_url: creator.avatar_url,
  }));

  campaignCollection.update((query) => query.where({ id: campaignId }), {
    data(record) {
      record.creator_count = creators.length;
      record.post_count = posts.length;
      record.tracked_item_count = nextTrackedItemCount;
      record.platforms = platforms;
      record.creator_preview_avatars = previewCreators;
      record.updated_at = new Date().toISOString();
      if (record.status !== 'scheduled') {
        record.last_collected_at = new Date().toISOString();
      }
    },
  });

  return campaignCollection.findFirst((query) => query.where({ id: campaignId }));
}

function mapCampaignToDTO(campaign: ReturnType<typeof campaignCollection.findMany>[number]) {
  return {
    id: campaign.id,
    name: campaign.campaign_name,
    start_date: campaign.campaign_start_date,
    end_date: campaign.campaign_end_date ?? undefined,
    keywords: campaign.keywords ?? [],
    hashtags: campaign.hashtags ?? [],
    mentions: campaign.mentions ?? [],
    creator_count: campaign.creator_count,
    post_count: campaign.post_count,
    tracked_item_count: campaign.tracked_item_count,
    platforms: campaign.platforms,
    status: campaign.status as CampaignStatus,
    created_at: campaign.created_at,
    updated_at: campaign.updated_at,
    last_collected_at: campaign.last_collected_at ?? undefined,
  };
}

function buildPostMetrics(index: number, platform: SOURCE_PLATFORM_TYPE) {
  const views = 18000 + index * 4200;
  const likes = 900 + index * 180;
  const comments = 70 + index * 16;
  const shares = platform === SOURCE_PLATFORM_TYPE.TIKTOK ? 45 + index * 12 : 0;
  const engagements = likes + comments + shares;
  const er = views === 0 ? 0 : (engagements / views) * 100;

  return {
    comments,
    engagements,
    er,
    likes,
    shares,
    views,
  };
}

function resolveCampaignLabel(value: string | null | undefined): CAMPAIGN_LABEL_TYPE | undefined {
  return Object.values(CAMPAIGN_LABEL_TYPE).find((label) => label === value);
}

function buildCampaignPostListItem(post: {
  id: string;
  creator_id: string;
  creator_name: string;
  creator: {
    avatar_url?: string;
    handle: string;
    profile_url: string;
  };
  platform: SOURCE_PLATFORM_TYPE;
  caption: string;
  post_url: string;
  posted_at: string;
  metrics_updated_at: string;
  metrics: {
    comments: number;
    er: number;
    likes: number;
    shares: number;
    views: number;
  };
}) {
  const creatorSeed = post.creator_id.length + post.creator_name.length;

  return {
    _id: post.id,
    date: new Date(post.posted_at).getTime(),
    author: {
      author_id: post.creator_id,
      follower_count: 10000 + creatorSeed * 73,
      following_count: 180 + (creatorSeed % 35),
      nickname: post.creator_name,
      profile_image_url: post.creator.avatar_url ?? '',
      profile_url: post.creator.profile_url,
      region: 'US',
      total_like_count: 20000 + creatorSeed * 110,
      username: post.creator.handle.replace(/^@/, ''),
    },
    source: {
      id: `campaign-source-${post.id}`,
      platform: post.platform,
      url: post.post_url,
    },
    extraction: {
      content: post.caption,
      engagement: {
        view: post.metrics.views,
        like: post.metrics.likes,
        comment: post.metrics.comments,
        shared: post.metrics.shares,
        rate: post.metrics.er,
      },
      hashtags: [],
      location: '',
      video: {
        poster_url: `https://picsum.photos/seed/${encodeURIComponent(post.id)}/640/800`,
      },
    },
    scraped_at: new Date(post.metrics_updated_at).getTime(),
    updated_at: post.metrics_updated_at,
    board_count: 0,
  };
}

function buildProfileUrl(platform: SOURCE_PLATFORM_TYPE, handle: string) {
  if (platform === SOURCE_PLATFORM_TYPE.INSTAGRAM) return `https://instagram.com/${handle}`;
  if (platform === SOURCE_PLATFORM_TYPE.YOUTUBE) return `https://youtube.com/@${handle}`;
  return `https://tiktok.com/@${handle}`;
}

function buildPostUrl(platform: SOURCE_PLATFORM_TYPE, handle: string, index: number) {
  if (platform === SOURCE_PLATFORM_TYPE.INSTAGRAM) {
    // Instagram reel shortcodes must be 11 characters to pass campaign URL validation.
    const reelId = `${handle.replace(/[^A-Za-z0-9_-]/g, '-')}${index}`.padStart(11, '0').slice(-11);
    return `https://instagram.com/reel/${reelId}`;
  }
  if (platform === SOURCE_PLATFORM_TYPE.YOUTUBE) {
    return `https://youtube.com/shorts/${handle}${index}`;
  }
  // TikTok video IDs must be 19 digits to pass campaign URL validation.
  return `https://tiktok.com/@${handle}/video/7${String(10000 + index).padStart(18, '0')}`;
}

function normalizeHandle(value: string) {
  return value.replace(/^@+/, '').trim();
}
